import { useEffect, useState } from "react";
import {
    Column,
    Container,
    Divider,
    Row,
    SingleScrollView,
    SizedBox,
} from "../../../common/components/CommonBaseComponents";
import { C1Text, C3Text, C4Text } from "../../../common/components/text/CustomText";
import CustomNetworkImage from "../../../common/components/image/CustomNetworkImage";
import CustomIcon from "../../../common/components/image/CustomIcon";
import styled from "styled-components";
import { ShopStatus, StatusBadge } from "../../../common/components/table/CustomTable";
import { useNavigate } from "react-router-dom";
import { useDevice } from "../../../common/hooks/useDevice";
import { Theme } from "../../../common/Theme";
import DashboardService from "../../../service/DashboardService";

function BookmarkedShopDashboardCard() {
    const service = new DashboardService();
    const [shops, setShops] = useState([]);

    const fetchBookmarkedShops = () => {
        service
            .getBookmarkedShops()
            .then((result) => {
                setShops(result);
            })
            .catch((error) => {
                console.error("Error fetching bookmarked shops:", error);
            });
    };

    useEffect(() => {
        fetchBookmarkedShops();
    }, []);

    return (
        <SizedBox
            border={`1px solid ${Theme.color.gray250}`}
            borderRadius={"16px"}
            width={"100%"}
            height={"915px"}
            alignItem={"flex-start"}
            justifyContent={"flex-start"}
            padding={"16px"}
            backgroundColor={"transparent"}
        >
            <Row>
                <C1Text>Bookmarked Shop</C1Text>
                <SizedBox width={"8px"} />
                <Container padding={"4.5px 10px"} borderRadius={"50px"} border={`1px solid ${Theme.color.neutral200}`}>
                    <C4Text fontColor={Theme.color.zinc850} fontWeight={"400"}>
                        {shops.length}
                    </C4Text>
                </Container>
            </Row>
            <SizedBox height={"12px"} />
            <SingleScrollView $flex={"1"} $overflowY={"auto"} $cursor={"pointer"} $minHeight={"0"}>
                {shops.map((shop, index) => (
                    <ShopListCard
                        key={index}
                        isLast={index === shops.length - 1}
                        shopCode={shop.shopCode}
                        shop={shop}
                    />
                ))}
            </SingleScrollView>
        </SizedBox>
    );
}

export default BookmarkedShopDashboardCard;

function ShopListCard({ isLast = false, shopCode = "LGF001", shop = {} }) {
    const navigation = useNavigate();
    const currentDevice = useDevice();

    if (currentDevice === "mobile") {
        return (
            <>
                <Column
                    width={"100%"}
                    cursor={"pointer"}
                    onClick={() => {
                        navigation(`/shops/${shopCode}`);
                    }}
                >
                    <Row width={"100%"} cursor={"pointer"}>
                        <ImageContainer>
                            <CustomNetworkImage
                                src={shop.imgeUrl ?? "https://picsum.photos/id/100/200/300"}
                                width={"110px"}
                                height={"110px"}
                                borderRadius={"12px"}
                                cursor={"pointer"}
                            />
                            <IconWrapper>
                                <CustomIcon width={"14px"} height={"18px"} isActive={true} iconName={"bookmark"} />
                            </IconWrapper>
                        </ImageContainer>
                        <SizedBox width={"16px"} />
                        <Column alignItem={"flex-start"} cursor={"pointer"}>
                            <C1Text>{shop.storeName ?? ""}</C1Text>
                            <SizedBox height={"6px"} />
                            <C4Text fontColor={Theme.color.gray500}>
                                {shop.address ?? ""}
                            </C4Text>
                            <SizedBox height={"12px"} />
                            <Row>
                                <ShopStatus status={shop.status ?? "Open"} />
                                <SizedBox width={"6px"} />
                                <StatusBadge>{shop.shopCode ?? ""}</StatusBadge>
                            </Row>
                        </Column>
                    </Row>
                    <SizedBox height={"12px"} />
                    <Row width={"100%"} alignItem={"flex-start"} justifyContent={"flex-start"}>
                        <Column width={"15%"} alignItem={"flex-start"}>
                            <C3Text fontColor={Theme.color.gray500} fontWeight={"500"}>
                                Owner
                            </C3Text>
                            <SizedBox height={"6px"} />
                            <C3Text fontColor={Theme.color.gray500} fontWeight={"500"}>
                                Contact
                            </C3Text>
                        </Column>
                        <Column width={"85%"} alignItem={"flex-start"}>
                            <C3Text fontWeight={"500"}>{shop.owner ?? ""}</C3Text>
                            <SizedBox height={"6px"} />
                            <C3Text fontColor={Theme.color.gray500} fontWeight={"500"}>
                                {shop.contact ?? ""}
                            </C3Text>
                        </Column>
                    </Row>
                    <SizedBox height={"12px"} />
                    <SizedBox
                        border={`1px solid ${Theme.color.stone50}`}
                        borderRadius={"8px"}
                        width={"100%"}
                        height={"100%"}
                        alignItem={"flex-start"}
                        justifyContent={"space-between"}
                        padding={"12px 16px"}
                        backgroundColor={Theme.color.stone50}
                        flex={1}
                    >
                        <Row width={"100%"} justifyContent={"space-between"}>
                            <Row>
                                <CustomIcon size={"24px"} iconName={"washer_circle"} />
                                <SizedBox width={"9px"} />
                                <C3Text fontColor={Theme.color.gray500}>Washer</C3Text>
                                <SizedBox width={"9px"} />
                                <CustomIcon size={"18px"} iconName={"alert_triangle"} />
                                <SizedBox width={"4px"} />
                                <C3Text fontColor={Theme.color.rose500} fontWeight={"500"}>
                                    {shop.waherErr ?? 0}
                                </C3Text>
                            </Row>
                            <Row>
                                <C3Text fontColor={Theme.color.zinc850} fontWeight={"700"}>
                                    {shop.washer ?? 0}
                                </C3Text>
                                <C3Text fontColor={Theme.color.gray800} fontWeight={"400"}>
                                    /{shop.washerTotal ?? 0}
                                </C3Text>
                            </Row>
                        </Row>
                        <SizedBox height={"6px"} />
                        <Row width={"100%"} justifyContent={"space-between"}>
                            <Row>
                                <CustomIcon size={"24px"} iconName={"dryer_circle"} />
                                <SizedBox width={"9px"} />
                                <C3Text fontColor={Theme.color.gray500}>Dryer</C3Text>
                                <SizedBox width={"9px"} />
                                <CustomIcon size={"18px"} iconName={"alert_triangle"} />
                                <SizedBox width={"4px"} />
                                <C3Text fontColor={Theme.color.rose500} fontWeight={"500"}>
                                    {shop.dryerErr ?? 0}
                                </C3Text>
                            </Row>
                            <Row>
                                <C3Text fontColor={Theme.color.zinc850} fontWeight={"700"}>
                                    {shop.dryer ?? 0}
                                </C3Text>
                                <C3Text fontColor={Theme.color.gray800} fontWeight={"400"}>
                                    /{shop.dryerTotal ?? 0}
                                </C3Text>
                            </Row>
                        </Row>
                        <SizedBox height={"6px"} />
                        <Row width={"100%"} justifyContent={"space-between"}>
                            <Row>
                                <CustomIcon size={"24px"} iconName={"combo_circle"} />
                                <SizedBox width={"9px"} />
                                <C3Text fontColor={Theme.color.gray500}>Combo</C3Text>
                                <SizedBox width={"9px"} />
                                <CustomIcon size={"18px"} iconName={"alert_triangle"} />
                                <SizedBox width={"4px"} />
                                <C3Text fontColor={Theme.color.rose500} fontWeight={"500"}>
                                    {shop.comboErr ?? 0}
                                </C3Text>
                            </Row>
                            <Row>
                                <C3Text fontColor={Theme.color.zinc850} fontWeight={"700"}>
                                    {shop.combo ?? 0}
                                </C3Text>
                                <C3Text fontColor={Theme.color.gray800} fontWeight={"400"}>
                                    /{shop.comboTotal ?? 0}
                                </C3Text>
                            </Row>
                        </Row>
                    </SizedBox>
                </Column>
                {!isLast && <Divider marginTop={"19px"} marginBottom={"19px"} />}
            </>
        );
    }

    return (
        <>
            <Row
                width={"100%"}
                cursor={"pointer"}
                onClick={() => {
                    navigation(`/shops/${shopCode}`);
                }}
            >
                <ImageContainer>
                    <CustomNetworkImage
                        src={shop.imgeUrl ?? "https://picsum.photos/id/100/200/300"}
                        width={"110px"}
                        height={"110px"}
                        borderRadius={"12px"}
                        cursor={"pointer"}
                    />
                    <IconWrapper>
                        <CustomIcon width={"14px"} height={"18px"} isActive={true} iconName={"bookmark"} />
                    </IconWrapper>
                </ImageContainer>
                <SizedBox width={"16px"} />
                <Column alignItem={"flex-start"} width={"33%"} cursor={"pointer"}>
                    <C1Text>{shop.storeName ?? ""}</C1Text>
                    <SizedBox height={"6px"} />
                    <C4Text fontColor={Theme.color.gray500}>
                        {shop.address ?? ""}
                    </C4Text>
                    <SizedBox height={"12px"} />
                    <Row>
                        <ShopStatus status={shop.status ?? "Open"} />
                        <SizedBox width={"6px"} />
                        <StatusBadge>{shop.shopCode ?? ""}</StatusBadge>
                    </Row>
                </Column>
                <Column alignItem={"flex-start"} width={"20%"} cursor={"pointer"}>
                    <C3Text fontColor={Theme.color.gray500} fontWeight={"500"}>
                        Owner
                    </C3Text>
                    <SizedBox height={"6px"} />
                    <C3Text fontWeight={"500"}>{shop.owner ?? ""}</C3Text>
                    <SizedBox height={"12px"} />
                    <C3Text fontColor={Theme.color.gray500} fontWeight={"500"}>
                        Contact
                    </C3Text>
                    <SizedBox height={"6px"} />
                    <C3Text fontWeight={"500"} fontColor={Theme.color.gray500}>
                        {shop.contact ?? ""}
                    </C3Text>
                </Column>
                <SizedBox
                    border={`1px solid ${Theme.color.stone50}`}
                    borderRadius={"8px"}
                    height={"100%"}
                    alignItem={"flex-start"}
                    justifyContent={"space-between"}
                    padding={"12px 16px"}
                    backgroundColor={Theme.color.stone50}
                    flex={1}
                >
                    <Row width={"100%"} justifyContent={"space-between"}>
                        <Row>
                            <CustomIcon size={"24px"} iconName={"washer_circle"} />
                            <SizedBox width={"9px"} />
                            <C3Text fontColor={Theme.color.gray500}>Washer</C3Text>
                            <SizedBox width={"9px"} />
                            <CustomIcon size={"18px"} iconName={"alert_triangle"} />
                            <SizedBox width={"4px"} />
                            <C3Text fontColor={Theme.color.rose500} fontWeight={"500"}>
                                {shop.waherErr ?? 0}
                            </C3Text>
                        </Row>
                        <Row>
                            <C3Text fontColor={Theme.color.zinc850} fontWeight={"700"}>
                                {shop.washer ?? 0}
                            </C3Text>
                            <C3Text fontColor={Theme.color.gray800} fontWeight={"400"}>
                                /{shop.washerTotal ?? 0}
                            </C3Text>
                        </Row>
                    </Row>
                    <SizedBox height={"6px"} />
                    <Row width={"100%"} justifyContent={"space-between"}>
                        <Row>
                            <CustomIcon size={"24px"} iconName={"dryer_circle"} />
                            <SizedBox width={"9px"} />
                            <C3Text fontColor={Theme.color.gray500}>Dryer</C3Text>
                            <SizedBox width={"9px"} />
                            <CustomIcon size={"18px"} iconName={"alert_triangle"} />
                            <SizedBox width={"4px"} />
                            <C3Text fontColor={Theme.color.rose500} fontWeight={"500"}>
                                {shop.dryerErr ?? 0}
                            </C3Text>
                        </Row>
                        <Row>
                            <C3Text fontColor={Theme.color.zinc850} fontWeight={"700"}>
                                {shop.dryer ?? 0}
                            </C3Text>
                            <C3Text fontColor={Theme.color.gray800} fontWeight={"400"}>
                                /{shop.dryerTotal ?? 0}
                            </C3Text>
                        </Row>
                    </Row>
                    <SizedBox height={"6px"} />
                    <Row width={"100%"} justifyContent={"space-between"}>
                        <Row>
                            <CustomIcon size={"24px"} iconName={"combo_circle"} />
                            <SizedBox width={"9px"} />
                            <C3Text fontColor={Theme.color.gray500}>Combo</C3Text>
                            <SizedBox width={"9px"} />
                            <CustomIcon size={"18px"} iconName={"alert_triangle"} />
                            <SizedBox width={"4px"} />
                            <C3Text fontColor={Theme.color.rose500} fontWeight={"500"}>
                                {shop.comboErr ?? 0}
                            </C3Text>
                        </Row>
                        <Row>
                            <C3Text fontColor={Theme.color.zinc850} fontWeight={"700"}>
                                {shop.combo ?? 0}
                            </C3Text>
                            <C3Text fontColor={Theme.color.gray800} fontWeight={"400"}>
                                /{shop.comboTotal ?? 0}
                            </C3Text>
                        </Row>
                    </Row>
                </SizedBox>
            </Row>
            {!isLast && <Divider marginTop={"19px"} marginBottom={"19px"} />}
        </>
    );
}

const ImageContainer = styled.div`
    position: relative;
    width: 110px;
    height: 110px;
    cursor: pointer;
`;

const IconWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: ${Theme.color.white_03};
    backdrop-filter: blur(1px);
    height: 36px;
    width: 36px;
    border-radius: 12px 0;
`;
